import { InspectionDetailNew } from "../../_objects/inspection";
import { SubjectNew } from "../../_objects/subject";
import { SubjectLocation, SubjectModelNew } from "../../_objects/subject-model";
import { InspectionSchema } from "../../_offline/models/inspection.schema";
import { SubjectSchema } from "../../_offline/models/subject.schema";
import { SubjectModelLocationsSchema } from "../../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../../_offline/models/subjectModel.schema";
import { BookMarkOption, InspectionImageCreate, UploadOption } from "../../_objects/inspection-image";
import { InspectionImageSchema } from "../../_offline/models/inspectionImage.schema";
import { ReportTemplateNew, TemplateDefinition } from "../../_offline/models/report-preview/report-template";
import { IndividualReportTemplateSchema } from "../../_offline/models/individual-report-template.schema";
import { IndividualReportSchema } from "../../_offline/models/individual-report.schema";
import { InspectionReportNew, ReportLocation } from "../../_objects/inspection-report";

export const toSubjectModelDto = (model: SubjectModelSchema): SubjectModelNew => {
  const newSubjectModel = new SubjectModelNew();
  newSubjectModel.inspectionType = 0;
  newSubjectModel.isValid = model.isValid;
  newSubjectModel.locations = toSubjectLocations(model.subjectModelLocations);
  newSubjectModel.name = model.name;
  newSubjectModel.schemaVersion = "1";
  return newSubjectModel;
};

const toSubjectLocations = (locationsSchema: SubjectModelLocationsSchema[]): SubjectLocation[] => {
  const locations = locationsSchema.find((loc) => loc.isNewestVersion).locations;
  return JSON.parse(locations) as SubjectLocation[];
};

export const toNewSubjectDto = (subjectSchema: SubjectSchema, createdModelId: string): SubjectNew => {
  return {
    ...subjectSchema,
    subjectModelId: createdModelId
  };
};

export const toNewReportDto = (
  reportSchema: IndividualReportSchema,
  createdInspectionId: string,
  createdReportTemplateId: string
): InspectionReportNew => {
  return {
    title: reportSchema.title,
    templateId: createdReportTemplateId,
    inspectionIds: [createdInspectionId],
    reportLocations: reportSchema.reportLocations ? (JSON.parse(reportSchema.reportLocations) as ReportLocation[]) : [],
    isAutoInputTitle: reportSchema.isAutoInputTitle ?? false,
    schemaVersion: reportSchema.schemaVersion,
    status: reportSchema.status,
    isSelectedImage: true
  };
};

export const toReportTemplateDto = (reportTemplateSchema: IndividualReportTemplateSchema, createdModelIds: string[]): ReportTemplateNew => {
  return {
    name: reportTemplateSchema.name,
    subjectModelIds: createdModelIds,
    template: reportTemplateSchema.template ? rewriteTemplateImageSrcs(JSON.parse(reportTemplateSchema.template) as TemplateDefinition) : null,
    isValid: reportTemplateSchema.isValid,
    reportTitleInputMode: reportTemplateSchema.reportTitleInputMode,
    reportTitleFormat: reportTemplateSchema.reportTitleFormat ? JSON.parse(reportTemplateSchema.reportTitleFormat) : [],
    schemaVersion: reportTemplateSchema.schemaVersion
  };
};

/**
 * The cloud renderer resolves a template image by the value stored in `TemplateImage.src`, keyed under the
 * template's own id. Locally stored templates keep paths like `reportTemplates/<local-id>/images/<file>` (or a
 * downloaded SAS URL), which no longer exist once the template is re-created in the cloud under a new id.
 * Reducing `src` to the bare file name matches the online RTB convention and the file names uploaded via TP11.
 */
const rewriteTemplateImageSrcs = (template: TemplateDefinition): TemplateDefinition => {
  const sections = [template.header, template.footer, ...(template.bodies ?? [])];
  for (const section of sections) {
    if (!section) continue;
    section.images?.forEach((image) => (image.src = toBareFileName(image.src)));
    const dataPlateImage = section.dataPlates?.imageInfo?.image;
    if (dataPlateImage) {
      dataPlateImage.src = toBareFileName(dataPlateImage.src);
    }
  }
  return template;
};

const toBareFileName = (src: string): string => {
  if (!src) return src;
  const withoutQuery = src.split(/[?#]/)[0];
  return withoutQuery.split(/[\\/]/).pop() || src;
};

export const toInspectionImageCreate = (inspectionImageSchema: InspectionImageSchema, inspectionId: string, file: File): InspectionImageCreate => {
  return {
    inspectionId: inspectionId,
    fileName: inspectionImageSchema.fileName,
    mimeType: inspectionImageSchema.mimeType,
    folderName: inspectionImageSchema.subjectLocationId ?? getFoldername(inspectionImageSchema.fileName),
    file: file,
    locationName: inspectionImageSchema.subjectLocationId,
    bookMarkOption: inspectionImageSchema.bookMarkOption ? (JSON.parse(inspectionImageSchema.bookMarkOption) as BookMarkOption[]) : [],
    uploadOption: UploadOption.None,
    deviceFileUri: null,
    audioFile: undefined,
    isExistsAudio: false
  };
};

const getFoldername = (fileName: string) => {
  if (!fileName) return "";

  const filePath = fileName.split("/").reverse();
  if (filePath.length >= 2) {
    return filePath[1];
  }
};

export const toInspectionDetailNew = (inspectionSchema: InspectionSchema, createdSubjectId: string, userId: string): InspectionDetailNew => {
  return {
    inspectionDate: inspectionSchema.inspectionDate,
    title: inspectionSchema.title,
    inspectors: [{ id: userId, isActive: true, isSubscription: true }],
    subjectId: createdSubjectId,
    status: inspectionSchema.status,
    schemaVersion: inspectionSchema.schemaVersion,
    imageCount: inspectionSchema.imageCount,
    connectorTenantId: inspectionSchema.connectorTenantId,
    reportCount: inspectionSchema.reportCount
  };
};
